import {
  fileTypeValidator,
  maxNumberOfColoursValidator,
  spotColorsValidator,
  resolutionValidator,
} from './index';
import { ProductPrintPositionAddModel } from '@/typings';
import { ProductPrintData } from '@midocean/webapps-shared-domain';
import { AnnotationShape } from '../typings/common';
import { useStore as useProductStore } from '@/stores/v2/product';
import { NotificationResult } from '../typings/notification';

export const constructArtworkValidationPipeline = (
  productPrintData: ProductPrintData,
  selectedPrintPositions: ProductPrintPositionAddModel[],
  shapes: AnnotationShape[],
  positionShapeMap: Record<string, string>[]
): NotificationResult[] => {
  const results = [];

  for (const shape of shapes) {
    if (shape.kind === 'Logo') {
      const shapePositionId = positionShapeMap.find(
        (x) => x.logoId === shape.id
      );
      if (shapePositionId && shapePositionId.id) {
        const productPrintPosition =
          productPrintData.productPrintPositions.find(
            (x) => x.id === shapePositionId.id
          );
        if (productPrintPosition) {
          const selectedTechniqueId = selectedPrintPositions.find(
            (x) => x.positionId === productPrintPosition.id
          )?.technique.id;
          const printTechnique = productPrintPosition.printTechniques.find(
            (x) => x.id === selectedTechniqueId
          );
          if (printTechnique) {
            for (const logoValidator of printTechnique.logoValidators) {
              if (logoValidator.type.trim() === 'FileType') {
                results.push(
                  fileTypeValidator(
                    logoValidator.configuration,
                    shape,
                    productPrintPosition.name
                  )
                );
              }

              if (
                logoValidator.type.trim() === 'Resolution' &&
                shape.kind === 'Logo' &&
                !shape.isVectorFileTrueVectorFile &&
                shape.pngHorizontalResolutionDPIs &&
                shape.pngVerticalResolutionDPIs
              ) {
                results.push(
                  resolutionValidator(
                    logoValidator.configuration,
                    shape,
                    productPrintPosition.name
                  )
                );
              }

              if (logoValidator.type.trim() === 'SpotColorsIncluded') {
                results.push(
                  spotColorsValidator(shape, productPrintPosition.id)
                );
              }
            }
          }
        }
      }
    }
  }

  for (const position of selectedPrintPositions) {
    const productPrintPosition = productPrintData.productPrintPositions.find(
      (x) => x.id === position.positionId
    );
    if (productPrintPosition) {
      const selectedTechniqueId = selectedPrintPositions.find(
        (x) => x.positionId === productPrintPosition.id
      )?.technique.id;
      const printTechnique = productPrintPosition.printTechniques.find(
        (x) => x.id === selectedTechniqueId
      );
      if (printTechnique) {
        const positionShapes = shapes.filter((x) =>
          positionShapeMap.some(
            (psm) => psm.id === position.positionId && psm.logoId === x.id
          )
        );

        if (positionShapes.length > 0) {
          const productStore = useProductStore();
          const doCheckMaxColors =
            productStore.isFullColor(position.positionId) ||
            productStore.isUncoloredConverterUsed(position.positionId);

          results.push(
            maxNumberOfColoursValidator(
              positionShapes,
              printTechnique.maximumColours,
              position.positionName,
              !doCheckMaxColors
            )
          );
        }
      }
    }
  }

  return results.filter((x): x is NotificationResult => x !== undefined);
};
